import { useDebouncedValue } from "@tanstack/react-pacer";
/* eslint-disable react-doctor/no-adjust-state-on-prop-change, react-doctor/no-cascading-set-state, react-hooks-js/set-state-in-effect */
import type {
    LocationSelection,
    SuggestedLocation,
} from "./location-selection";
import { useGooglePlaces } from "../location/useGooglePlaces";

type UseLocationAutocompleteOptions = {
    inputValue: string;
    isOpen?: boolean;
    lat?: number;
    lng?: number;
    prefetchInput?: string;
    includeNearbySuggestions?: boolean;
    shouldDebounce?: boolean;
    debounceMs?: number;
};

type PlaceSuggestion = {
    place_id: string;
    description: string;
};

function toSuggestedLocations(suggestions: PlaceSuggestion[]) {
    return suggestions.reduce<Array<{ id: string; label: string }>>(
        (acc, suggestion) => {
            const next = {
                id: suggestion.place_id,
                label: suggestion.description,
            };
            if (next.id && next.label) acc.push(next);
            return acc;
        },
        [],
    );
}

export function useLocationAutocomplete({
    inputValue,
    isOpen = true,
    lat,
    lng,
    prefetchInput,
    includeNearbySuggestions = false,
    shouldDebounce = true,
    debounceMs = 300,
}: UseLocationAutocompleteOptions) {
    const trimmedInput = inputValue.trim();
    const isAutocompleteMode = trimmedInput.length >= 3;
    const [delayedInput] = useDebouncedValue(trimmedInput, {
        wait: shouldDebounce ? debounceMs : 0,
    });
    const debouncedInput = trimmedInput.length >= 3 ? delayedInput : "";

    const {
        suggestions,
        nearbySuggestions,
        isLoading,
        getPlaceDetails,
        getCurrentLocation,
        reverseGeocode,
    } = useGooglePlaces({
        autocompleteInput: debouncedInput,
        autocompleteEnabled: isOpen,
        nearbyLat: lat,
        nearbyLng: lng,
        nearbyEnabled:
            includeNearbySuggestions && isOpen && !isAutocompleteMode,
        prefetchInput: prefetchInput ?? inputValue,
    });

    const autocompleteSuggestions = toSuggestedLocations(suggestions);
    const nearbyLocationSuggestions = toSuggestedLocations(nearbySuggestions);
    const suggestedLocations = isAutocompleteMode
        ? autocompleteSuggestions
        : nearbyLocationSuggestions;

    const getLocationFromSuggestion = async (
        suggestion: SuggestedLocation,
    ): Promise<LocationSelection | null> => {
        const location = await getPlaceDetails(suggestion.id);
        if (!location) {
            return null;
        }

        return {
            city: location.address?.city || undefined,
            lat: location.lat,
            lng: location.lng,
            label: location.displayName || suggestion.label,
            state: location.address?.region || location.state,
        };
    };

    const getCurrentLocationSelection =
        async (): Promise<LocationSelection | null> => {
            const location = await getCurrentLocation();
            if (!location) {
                return null;
            }

            return {
                city: location.address?.city || undefined,
                lat: location.lat,
                lng: location.lng,
                label: location.displayName,
                state: location.address?.region || location.state,
            };
        };

    return {
        isLoading,
        isAutocompleteMode,
        autocompleteSuggestions,
        nearbyLocationSuggestions,
        suggestedLocations,
        getLocationFromSuggestion,
        getCurrentLocationSelection,
        reverseGeocodeLocation: reverseGeocode,
    };
}
